import {
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";


import {
  Card,
  Input,
  Space,
  Button,
  Header,
  LicensePlateInput,
  Text,
} from "../../Components/Index";
import CameraViewport from "../PlateCapture/components/CameraViewport";
import CaptureError from "../PlateCapture/components/CaptureError";
import GalleryFileInput from "../PlateCapture/components/GalleryFileInput";
import ImagePreview from "../PlateCapture/components/ImagePreview";
import useCamera from "../PlateCapture/hooks/useCamera";
import { captureVideoFrame, createImageUrl, validateImageFile } from "../PlateCapture/imageUtils";
import { recognizePlate } from "../../services/api/plateRecognitionApi";
import { extractPlateFromResponse } from "../PlateCapture/plateUtils";
import VehicleColorPicker from "../../Components/VehicleRegisterPopup/VehicleColorPicker";
import VehicleFormField from "../../Components/VehicleRegisterPopup/VehicleFormField";
import VehicleYearPicker from "../../Components/VehicleRegisterPopup/VehicleYearPicker";
import { gregorianToJalaliYear, groupThousands, toPersianDigits } from "../../Components/VehicleRegisterPopup/vehicleRegisterUtils";

import { useCars } from "../../Context/CarContext";

import { extractErrorMessage } from "../../services/api/errorHandler";

// =========================
// تبدیل اعداد فارسی به انگلیسی
// =========================

const convertPersianNumber = (
  value
) => {
  return String(value)
    .replace(
      /[۰-۹]/g,
      (d) =>
        "۰۱۲۳۴۵۶۷۸۹".indexOf(d)
    )
    .replace(
      /[،,٬]/g,
      ""
    );
};

// =========================
// Parse پلاک
// =========================

const parsePlateString = (
  plateString
) => {
  if (!plateString) {
    return {
      plate1: "",
      plate2: "",
      plate3: "",
      plateEnd: "",
    };
  }

  const str =
    String(plateString).trim();

  const match = str.match(
    /^([\d\u06F0-\u06F9\s]+)\s*([^\d\s])\s*([\d\u06F0-\u06F9\s]+)\s*\|\s*([\d\u06F0-\u06F9]+)$/
  );

  if (match) {
    return {
      plate1:
        match[1].trim(),

      plate2:
        match[3].trim(),

      plate3:
        match[2].trim(),

      plateEnd:
        match[4].trim(),
    };
  }

  return {
    plate1: "",
    plate2: "",
    plate3: "",
    plateEnd: "",
  };
};

function AddCarCapture({ onComplete }) {
  const camera = useCamera();
  const fileInputRef = useRef(null);
  const [image, setImage] = useState(null);
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => () => {
    if (image?.url) URL.revokeObjectURL(image.url);
  }, [image]);

  const selectImage = file => {
    const validationError = validateImageFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    camera.stop();
    setImage({ file, url: createImageUrl(file) });
  };

  const takePhoto = async () => {
    try {
      selectImage(await captureVideoFrame(camera.videoRef.current));
    } catch (captureError) {
      setError(captureError.message || "گرفتن عکس ممکن نشد.");
    }
  };

  const processImage = async () => {
    if (!image?.file || processing) return;
    setProcessing(true);
    setError("");
    try {
      const response = await recognizePlate(image.file);
      const recognizedPlate = extractPlateFromResponse(response);
      if (!recognizedPlate) {
        throw new Error("پلاک از روی تصویر خوانده نشد؛ عکس واضح‌تری بگیرید یا دستی ادامه دهید.");
      }
      onComplete({ recognizedPlate });
    } catch (recognitionError) {
      setError(extractErrorMessage(recognitionError, recognitionError.message || "خواندن پلاک با خطا مواجه شد."));
    } finally {
      setProcessing(false);
    }
  };

  return <main className="plate-capture" dir="rtl">
    <Header hideR="" LinkR={-1} BackFallback="/Cars" Text="عکس و تشخیص پلاک خودرو" showNotification={false} />
    <div className="plate-capture__content">
      {!image ? (
        <CameraViewport
          camera={camera}
          onCapture={takePhoto}
          onSelectGallery={() => fileInputRef.current?.click()}
          onManualEntry={() => onComplete({})}
        />
      ) : (
        <ImagePreview
          imageUrl={image.url}
          file={image.file}
          processing={processing}
          onRetry={() => { setImage(null); setError(""); window.requestAnimationFrame(() => camera.start()); }}
          onSubmit={processImage}
        />
      )}
      <CaptureError message={error} />
      {!image && <GalleryFileInput inputRef={fileInputRef} onSelect={selectImage} />}
    </div>
  </main>;
}

// =========================
// Component
// =========================

function AddCars() {
  const navigate =
    useNavigate();

  const { carId } =
    useParams();

  const isEditMode =
    Boolean(carId);

  const [captureComplete, setCaptureComplete] = useState(isEditMode);

  const {
    addCar,
    editCar,
    getCarById,
    loading: carLoading,
  } = useCars();

  // =========================
  // Form
  // =========================

  const [
    formData,
    setFormData,
  ] = useState({
    carModel: "",
    productionYear: "",
    productionYearJalali: "",
    productionYearGregorian: "",
    carColor: "",

    // فقط همین فیلد برای کیلومتر
    currentMileage: "",

    chassisNumber: "",

    plate: "",
    plate1: "",
    plate2: "",
    plate3: "",
    plateEnd: "",
  });

  const [error, setError] =
    useState("");

  const completeCapture = ({ recognizedPlate = "" } = {}) => {
    if (recognizedPlate) {
      handleInputChange("plate", recognizedPlate);
    }
    setCaptureComplete(true);
  };

  // =========================
  // دریافت اطلاعات ماشین
  // =========================

  useEffect(() => {
    if (!isEditMode) {
      return;
    }

    const loadCar =
      async () => {
        try {
          setError("");

          const car =
            await getCarById(
              carId
            );

          if (!car) {
            setError(
              "اطلاعات ماشین پیدا نشد"
            );

            return;
          }

          console.log(
            "CAR FOR EDIT:",
            car
          );

          // =========================
          // پلاک
          // =========================

          const plateParts = {
            plate1:
              car.Plate1 ||
              car.plate1 ||
              "",

            plate2:
              car.Plate2 ||
              car.plate2 ||
              "",

            plate3:
              car.Plate3 ||
              car.plate3 ||
              "",

            plateEnd:
              car.End ||
              car.plateEnd ||
              "",
          };

          const combinedPlate =
            `${plateParts.plate1} ${plateParts.plate3} ${plateParts.plate2} | ${plateParts.plateEnd}`.trim();

          // =========================
          // کیلومتر
          // =========================

          const mileage =
            car.currentMileage ??
            0;

          console.log(
            "CAR MILEAGE:",
            mileage
          );

          // =========================
          // Form
          // =========================

          setFormData({
            carModel: String(
              car.title ||
                car.CarModel ||
                car.carModel ||
                car.model ||
                ""
            ),

            productionYear:
              String(
                car.buildYear ||
                  car.ProductionYear ||
                  car.productionYear ||
                  ""
              ),

            productionYearJalali: car.buildYear
              ? toPersianDigits(gregorianToJalaliYear(car.buildYear))
              : "",

            productionYearGregorian: car.buildYear
              ? toPersianDigits(car.buildYear)
              : "",

            carColor: String(
              car.color ||
                car.CarColor ||
                car.carColor ||
                ""
            ),

            // اینجا مقدار واقعی Backend
            currentMileage:
              String(mileage),

            chassisNumber:
              String(
                car.chassisNumber ||
                  ""
              ),

            plate:
              combinedPlate,

            plate1:
              plateParts.plate1,

            plate2:
              plateParts.plate2,

            plate3:
              plateParts.plate3,

            plateEnd:
              plateParts.plateEnd,
          });

          // =========================
          // Image
          // =========================

        } catch (err) {
          console.error(
            "GET CAR FOR EDIT ERROR:",
            err
          );

          setError(
            extractErrorMessage(
              err,
              "خطا در دریافت اطلاعات ماشین"
            )
          );
        }
      };

    loadCar();
  }, [
    carId,
    isEditMode,
    getCarById,
  ]);

  // =========================
  // Inputs
  // =========================

  const handleInputChange =
    useCallback(
      (
        field,
        value
      ) => {
        setFormData(
          (prev) => {
            const updated = {
              ...prev,

              [field]:
                String(value),
            };

            // =========================
            // پلاک
            // =========================

            if (
              field ===
              "plate"
            ) {
              const parsed =
                parsePlateString(
                  value
                );

              updated.plate1 =
                parsed.plate1;

              updated.plate2 =
                parsed.plate2;

              updated.plate3 =
                parsed.plate3;

              updated.plateEnd =
                parsed.plateEnd;
            }

            return updated;
          }
        );

        setError("");
      },
      []
    );

  const handleYearChange = ({ jalali, gregorian }) => {
    setFormData(previous => ({
      ...previous,
      productionYear: gregorian,
      productionYearJalali: jalali,
      productionYearGregorian: gregorian,
    }));
    setError("");
  };

  const handleMileageChange = event => {
    const digits = convertPersianNumber(event.target.value)
      .replace(/[^\d]/g, "")
      .slice(0, 10);
    handleInputChange(
      "currentMileage",
      digits ? toPersianDigits(groupThousands(digits)) : ""
    );
  };

  // =========================
  // Submit
  // =========================

  const handleSubmit =
    async (e) => {
      e.preventDefault();

      setError("");

      // =========================
      // Validation
      // =========================

      if (
        !String(
          formData.carModel
        ).trim()
      ) {
        setError(
          "مدل ماشین را وارد کنید"
        );

        return;
      }

      if (
        !String(
          formData.chassisNumber
        ).trim()
      ) {
        setError(
          "شماره شاسی را وارد کنید"
        );

        return;
      }

      try {
        // =========================
        // کیلومتر
        // =========================

        const mileageText =
          convertPersianNumber(
            formData.currentMileage
          );

        const currentMileage =
          parseInt(
            mileageText,
            10
          ) || 0;

        console.log(
          "FINAL CURRENT MILEAGE:",
          currentMileage
        );

        // =========================
        // سال ساخت
        // =========================

        const buildYear =
          parseInt(
            convertPersianNumber(
              formData.productionYear
            ),
            10
          ) || null;

        // =========================
        // Data
        // =========================

        const carData = {
          title:
            String(
              formData.carModel
            ).trim(),

          brand:
            String(
              formData.carModel
            )
              .trim()
              .split(" ")[0] ||
            "Unknown",

          // کیلومتر واقعی
          currentMileage,

          buildYear,

          color:
            String(
              formData.carColor
            ).trim() ||
            null,

          chassisNumber:
            String(
              formData.chassisNumber
            ).trim() ||
            null,

          plate1:
            String(
              formData.plate1
            ).trim() ||
            null,

          plate2:
            String(
              formData.plate2
            ).trim() ||
            null,

          plate3:
            String(
              formData.plate3
            ).trim() ||
            null,

          plateEnd:
            String(
              formData.plateEnd
            ).trim() ||
            null,

          // تصویر اسکن‌شده فقط برای تشخیص پلاک است و ذخیره نمی‌شود.
          image: null,
        };

        console.log(
          "FINAL CAR DATA:",
          carData
        );

        // =========================
        // Edit
        // =========================

        if (isEditMode) {
          if (!editCar) {
            throw new Error(
              "تابع editCar در CarContext پیدا نشد"
            );
          }

          await editCar(
            carId,
            carData
          );

          navigate(
            "/Cars"
          );

          return;
        }

        // =========================
        // Create
        // =========================

        await addCar(
          carData
        );

        navigate(
          "/Cars"
        );
      } catch (err) {
        console.error(
          isEditMode
            ? "EDIT CAR ERROR:"
            : "ADD CAR ERROR:",
          err
        );

        setError(
          extractErrorMessage(
            err,
            isEditMode
              ? "خطا در ویرایش ماشین. لطفاً دوباره تلاش کنید"
              : "خطا در ثبت ماشین. لطفاً دوباره تلاش کنید"
          )
        );
      }
    };

  // =========================
  // Loading
  // =========================

  if (!captureComplete) {
    return <AddCarCapture onComplete={completeCapture} />;
  }

  if (
    isEditMode &&
    carLoading &&
    !formData.carModel
  ) {
    return (
      <>
        <Header
          hideR=""
          LinkR={-1}
          BackFallback="/Cars"
          Text="ویرایش ماشین"
        />

        <Card>
          <Space size="xl" />

          <Text
            position="center"
            FontSize="md"
          >
            در حال دریافت اطلاعات ماشین...
          </Text>

          <Space size="xl" />
        </Card>
      </>
    );
  }

  // =========================
  // UI
  // =========================

  return (
    <>
      {/* Header */}

      <Header
        hideR=""
        LinkR={-1}
        BackFallback="/Cars"
        Text={
          isEditMode
            ? "ویرایش ماشین"
            : "اضافه کردن ماشین"
        }
      />

      <Card className="add-cars__form-card">
        <Space />

        {/* Car Model */}

        <Input
          label="مدل ماشین"
          value={
            formData.carModel
          }
          onChange={(e) =>
            handleInputChange(
              "carModel",
              e.target.value
            )
          }
          plac="مثال: پژو ۲۰۶ تیپ ۵"
        />

        <Space />

        {/* Production Year */}

        <div className="add-cars__year-picker">
          <VehicleYearPicker
            jalaliValue={formData.productionYearJalali}
            gregorianValue={formData.productionYearGregorian}
            onChange={handleYearChange}
          />
        </div>

        <Space />

        {/* Car Color */}

        <div className="add-cars__color-picker">
          <VehicleColorPicker
            value={formData.carColor}
            onChange={value => handleInputChange("carColor", value)}
          />
        </div>

        <Space />

        {/* Current Mileage */}

        <div className="add-cars__mileage-field">
          <VehicleFormField
            name="currentMileage"
            label="کیلومتر فعلی"
            value={formData.currentMileage}
            onChange={handleMileageChange}
            placeholder="مثال: ۵۰٬۰۰۰"
            numeric
            maxLength={13}
          />
        </div>

        <Space />

        {/* Chassis Number */}

        <Input
          label="شماره شاسی *"
          value={
            formData.chassisNumber
          }
          onChange={(e) =>
            handleInputChange(
              "chassisNumber",
              e.target.value
            )
          }
          plac="مثال: ۱۲۳۴۵۶۷۸۹"
        />

        <Space />

        {/* License Plate */}

        <div className="add-cars__plate-field">
          <div className="add-cars__plate-heading">
            <label className="add-cars__plate-label">
              شماره پلاک
            </label>
            <button
              type="button"
              className="add-cars__rescan-button"
              onClick={() => setCaptureComplete(false)}
            >
              اسکن مجدد پلاک
            </button>
          </div>

          <LicensePlateInput
            value={
              formData.plate
            }
            onChange={(
              value
            ) =>
              handleInputChange(
                "plate",
                value
              )
            }
          />
        </div>

        <Space />

        {/* Error */}

        {error && (
          <Text
            color="red"
            size="sm"
            position="right"
          >
            {error}
          </Text>
        )}

        <Space />

        {/* Submit */}

        <Button
          onClick={
            handleSubmit
          }
          type="submit"
          disabled={
            carLoading
          }
        >
          {carLoading
            ? isEditMode
              ? "در حال ویرایش..."
              : "در حال ثبت..."
            : isEditMode
              ? "ویرایش"
              : "تایید"}
        </Button>
      </Card>
    </>
  );
}

export default AddCars;
